import { useEffect, useState } from 'react';
import { Button, Dialog, ErrorState, TextSkeleton } from '@dru/ui';
import { Download, ExternalLink } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';

/**
 * A report PDF shown on the page, so reading it needs no download.
 *
 * The grant path mints a five-minute link; the bytes are fetched into a blob
 * URL so the frame never depends on the API allowing itself to be framed,
 * and Download saves the bytes already here instead of fetching again.
 */
export function PdfViewerDialog({ title, grantPath, fileName, onClose }: {
  title: string;
  grantPath: string;
  fileName: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let made: string | null = null;
    setUrl(null); setError(null);
    (async () => {
      const grant = await api.get<{ url: string }>(grantPath);
      const res = await fetch(grant.url, { credentials: 'include' });
      if (!res.ok) throw new Error('The report could not be fetched.');
      made = URL.createObjectURL(new Blob([await res.arrayBuffer()], { type: 'application/pdf' }));
      if (cancelled) { URL.revokeObjectURL(made); return; }
      setUrl(made);
    })().catch((e) => { if (!cancelled) setError(errorMessage(e)); });
    return () => { cancelled = true; if (made) URL.revokeObjectURL(made); };
  }, [grantPath, attempt]);

  function save() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName.replace(/[\\/]/g, '-')}.pdf`;
    a.click();
  }

  return (
    <Dialog
      title={title}
      onClose={onClose}
      document
      footer={(
        <>
          <Button variant="secondary" size="sm" icon={<ExternalLink className="h-4 w-4" />} disabled={!url}
            onClick={() => url && window.open(url, '_blank', 'noopener')}>
            Open in new tab
          </Button>
          <Button size="sm" icon={<Download className="h-4 w-4" />} disabled={!url} onClick={save}>
            Download
          </Button>
        </>
      )}
    >
      <div className="flex h-full min-h-[70vh] flex-col pt-1">
        {error ? <ErrorState title="The report could not be opened" detail={error} onRetry={() => setAttempt((n) => n + 1)} />
          : !url ? <TextSkeleton lines={4} />
          : <iframe title={title} src={url} className="min-h-[70vh] w-full flex-1 rounded-btn border border-neutral-200 bg-white" />}
      </div>
    </Dialog>
  );
}
